import { Grid, Typography, Tooltip } from '@mui/material';
import { Box } from '@mui/system';

import { useEffect, useState, useCallback } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import NotificationStatusIcon from 'components/Notification/NotificationStatusIcon';

import { ICreateTask, ITask } from 'common/api/type-def';
import ButtonComp from 'common/components/Button/ButtonComp';
import { ISelectOption } from 'common/components/SearchableSelectDropdown/SearchSelectDropdown';
import SelectDropdown from 'common/components/Select/SelectDropdown';
import SelectableHOC from 'common/components/SelectableHOC/SelectableHOC';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import {
	MODULES,
	MODULE_NAME,
	TABLE_VIEW_PAGE_SIZE,
	TASKTYPE,
} from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { ENTITY_TYPE } from 'common/constants/TaxonomyConstants';
import { LoggedInRole } from 'common/constants/UserManagementConstants';
import { RootState } from 'common/store/AppStore';
import {
	setRequirementInventoryBreadcrumbText,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { setWorkflowFeedback } from 'common/store/reducer/simulationReducer';
import { isRoleExists } from 'common/utils/authCheck';
import {
	dateFullYearFormat,
	getISODateString,
	urlWithTenant,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import {
	getAllBusinessUnits,
	getAssociatedAssignedToUsers,
	editBusinessUnits,
	updateBulkTask,
} from 'features/RegulationInventory/api';
import {
	IBusiness,
	IAssignedUsers,
	IGroupsTableList,
	IBusinessUnit,
	IRegInvOwnerInfo,
} from 'features/RegulationInventory/api/type-def';
import BusinessUnitDropdown, {
	IBUDropDownOptions,
} from 'features/RegulationInventory/Components/BusinessUnitDropDown/BusinessUnitDropDown';
import { getRegInvOwnerData } from 'features/RegulationInventory/RegInventoryUtils';

import styles from './MappingTable.module.scss';

const {
	ROLE_OPERATIONS_MANAGER,
	ROLE_SYSTEM_ADMINISTRATOR,
	ROLE_USER_ADMINISTRATOR,
} = LoggedInRole;

const { REQUIREMENT_INVENTORY_NAME } = MODULE_NAME;
const { TABLE_SIZE_12 } = TABLE_VIEW_PAGE_SIZE;

const { OK, BAD_REQUEST, NOT_FOUND, CREATED } = HTTP_STATUS_CODES;

const { regulationInventory, overView, regulationMappingAssessment } =
	rrcpDashboardRoutes;

interface IGroupsTableProps {
	id?: string;
	isLoading: boolean;
	inventoryTableData: IGroupsTableList[];
	getTableData: any;
	totalRecordsCount: number;
	errorMessage: string;
}

export interface IBusinessAssociation {
	businessUnit: string;
	assignedUsers: IAssignedUsers[];
}
const MappingTable = (props: IGroupsTableProps) => {
	const {
		id,
		isLoading,
		inventoryTableData,
		getTableData,
		totalRecordsCount,
		errorMessage,
	} = props;

	const checkRole = isRoleExists([
		ROLE_OPERATIONS_MANAGER,
		ROLE_SYSTEM_ADMINISTRATOR,
		ROLE_USER_ADMINISTRATOR,
	]);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
	const [trigger, setTrigger] = useState<number>(0);
	const [allBusinessUnits, setAllBusinessUnits] = useState<ISelectOption[]>([]);
	const [mappingOwnerList, setMappingOwnerList] = useState<IRegInvOwnerInfo[]>(
		[],
	);
	const [assignedUsers, setAssignedUsers] = useState<IBusinessAssociation[]>(
		[],
	);
	const [enableApplyButton, setEnableApplyButton] = useState<boolean>(false);
	const { tableQueryObj: tableProps, updateTableQuery: setTableProps } =
		useTableQuery({ id });
	const { updateQueryFlag } = useTableQuery({ id: 'AllRegTable' });

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [updatedBusinessUnits, setUpdatedBusinessUnits] = useState<
		IBUDropDownOptions[]
	>([]);
	const { updateTableQuery } = useTableQuery({
		id: 'relationshipCard',
		defaultPageSize: TABLE_SIZE_12,
	});

	const navigateToMappingDetails = (params: IGroupsTableList) => {
		dispatch(setWorkflowFeedback([]));
		updateQueryFlag(true);
		dispatch(
			setRequirementInventoryBreadcrumbText(MODULES.REQUIREMENT_INVENTORY),
		);
		navigate(
			urlWithTenant(
				`/${regulationInventory}/${overView}/${regulationMappingAssessment}/${params.groupId}`,
			),
		);
	};

	const handlePageChange = (data: any, flag?: boolean) => {
		if (flag) {
			getTableData(data);
			setTableProps(data);
		}
	};

	const handleSortChange = (data: any) => {
		getTableData(data);
		setTableProps(data);
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const getMappingOwnerData = async (status: string) => {
		if (mappingOwnerList.find((res) => res.status === status)) {
			return;
		}
		const tempData = await getRegInvOwnerData(status);
		if (tempData) {
			setMappingOwnerList((prev) => [...prev, tempData]);
		}
	};

	const onApplyClick = () => {
		const headers = createHeaderForMultipleCitations(
			selectedCitations,
			updatedBusinessUnits,
		);
		editBusinessUnitDetails(headers);
		onHandleCancel();
	};

	const onHandleCancel = () => {
		setSelectedGroups([]);
		setEnableApplyButton(false);
		setTrigger((trigger) => trigger + 1);
	};

	const getAssociatedBusinessUnitNames = (param: IGroupsTableList) =>
		param?.businessUnitDetailDTOS?.map((res: any) => ({
			id: res.businessUnitId,
			name: res.businessUnitName,
		})) || [];

	const getAssociatedAssignedUsers = (param: IGroupsTableList) =>
		param?.businessUnitDetailDTOS?.map((res: any) => ({
			id: res.businessUnitId,
			name: `${res.assignedUserName || '-'}`,
		})) || [];

	const selectedCitations = inventoryTableData.filter((res) =>
		selectedGroups.includes(res.groupId),
	);

	const getAllBusinessUnit = async () => {
		const response: any = await getAllBusinessUnits();
		if (response?.statusCode === OK) {
			const businessUnits: IBusiness[] = response?.responseBody;
			const values: ISelectOption[] = businessUnits.map((res) => ({
				id: res.businessUnitUserId,
				name: res.businessUnitName,
			}));
			setAllBusinessUnits(values);
		}
	};

	const getAssignedUserForSelectedBusinessUnit = async (value: string) => {
		const resp: any = await getAssociatedAssignedToUsers(value);
		if (resp?.statusCode === OK) {
			const users: IAssignedUsers[] = resp?.responseBody;
			return users;
		}
		return [];
	};

	useEffect(() => {
		getAllBusinessUnit();
	}, []);

	useEffect(() => {
		allBusinessUnits.forEach(async (res) => {
			const value = await getAssignedUserForSelectedBusinessUnit(res.id);
			setAssignedUsers((prev) => [
				...prev,
				{ businessUnit: res.name, assignedUsers: value },
			]);
		});
	}, [allBusinessUnits]);

	const onRowCheckboxSelect = (selectedId: any) => {
		if (selectedGroups.includes(selectedId)) {
			setSelectedGroups((res) => res.filter((val) => val !== selectedId));
		} else {
			setSelectedGroups(selectedId);
		}
	};

	const buHeaders = (obj: IGroupsTableList, val?: IBUDropDownOptions[]) => {
		const defaultValues =
			obj.businessUnitDetailDTOS?.map((res: any) => {
				const value = {
					businessUnitId: res.businessUnitId,
					assignedUserId: res.assignedUserId,
					businessUnitName: res.businessUnitName,
					assignedUserName: res.assignedUserName,
					taskStatus: obj.taskStatus || '',
					reviewDeadLine: getISODateString(obj.reviewByDate ?? ''),
				};
				return value;
			}) || [];
		if (val && val.length > 0) {
			const elements = val?.map((res) => {
				const value = {
					businessUnitId: res.businessUnitId,
					assignedUserId: res.assignedUserId,
					businessUnitName: res.businessUnit,
					assignedUserName: res.assignedUserName,
					taskStatus: obj.taskStatus || '',
					reviewDeadLine: getISODateString(obj.reviewByDate ?? ''),
				};
				return value;
			});
			return elements || [];
		}
		return defaultValues;
	};

	const createHeaders = (
		param: IGroupsTableList,
		val?: IBUDropDownOptions[],
	) => {
		const headers: IBusinessUnit[] = [];
		const ele: IBusinessUnit = {
			groupId: param?.groupId,
			groupName: param?.groupName,
			businessUnits: buHeaders(param, val),
			taskType: TASKTYPE.WORKFLOW,
			moduleName: REQUIREMENT_INVENTORY_NAME,
		};
		headers.push(ele);
		return headers;
	};

	const createHeaderForMultipleCitations = (
		params: IGroupsTableList[],
		val?: IBUDropDownOptions[],
	) => {
		const data = params.map((re) => {
			const headers: IBusinessUnit = {
				groupId: re.groupId,
				groupName: re.groupName,
				businessUnits: buHeaders(re, val),
				taskType: TASKTYPE.WORKFLOW,
				moduleName: REQUIREMENT_INVENTORY_NAME,
			};
			return headers;
		});
		return data;
	};

	const editBusinessUnitDetails = async (headers: IBusinessUnit[]) => {
		setUpdatedBusinessUnits([]);
		const resp: any = await editBusinessUnits(headers);
		if (resp?.statusCode === OK || resp?.statusCode === CREATED) {
			dispatch(
				setToastMessage({
					message: 'The Mapping Details Saved Successfully',
					severity: 'success',
					isToastOpen: true,
				}),
			);
			getTableData(tableProps);
		} else if (resp?.statusCode === BAD_REQUEST) {
			handleError(resp);
		}
	};
	const updateTableData = (response: ICreateTask) => {
		const taskList = response.taskResponseList || [];
		inventoryTableData.forEach((res) => {
			taskList.forEach((val: any) => {
				if (res.taskId === val.id) {
					res.assignedUserName = val.assignedUserFullName || '';
					res.assignedUserId = val.assignedUserId || '';
					res.taskId = val.id || '';
				}
			});
		});
	};

	const getMappingOwnerList = (status: string) => {
		let mappingOwnerValues: any = [];
		mappingOwnerList.forEach((res) => {
			if (res.status === status) {
				mappingOwnerValues = res.owners;
			}
		});
		return mappingOwnerValues;
	};

	const onMappingOwnerChange =
		(params: IGroupsTableList) => (value: string, obj: any) => {
			const taskObj = {
				id: params.taskId,
				...(obj ? { ...obj } : {}),
				assignedObjectType: ENTITY_TYPE.GROUP,
				status: params.taskStatus,
				assignedObjectId: params.groupId,
			} as unknown as ITask;
			updateGroupTask(taskObj);
		};

	const handleError = (resp: any) => {
		const { errorBody } = resp;
		const responseError: any = resp.error;
		const errorMsg =
			(errorBody?.errorMessage as string) || (responseError?.message as string);
		dispatch(
			setToastMessage({
				message: errorMsg,
				severity: 'error',
				isToastOpen: true,
			}),
		);
	};

	const updateGroupTask = async (taskObj: ITask) => {
		if (taskObj.id) {
			const response: any = await updateBulkTask([taskObj]);
			updateTableData(response?.responseBody);
			if (response) {
				if (response.statusCode === OK) {
					/// to-do remove displaying sucess. instead update the rows from response into selectedTheme
					dispatch(
						setToastMessage({
							message: 'Details saved successfully',
							severity: 'success',
							isToastOpen: true,
						}),
					);
				}
				if (
					response.statusCode === NOT_FOUND ||
					response.statusCode === BAD_REQUEST
				) {
					handleError(response);
				}
			}
		}
	};

	const saveMappingDetails = (
		param: IGroupsTableList,
		val?: IBUDropDownOptions[],
	) => {
		const headers: IBusinessUnit[] = createHeaders(param, val);
		editBusinessUnitDetails(headers);
	};

	const saveHandler = (val: IBUDropDownOptions[], enableApply: boolean) => {
		setUpdatedBusinessUnits(val);
		setEnableApplyButton(enableApply);
	};

	useEffect(() => {
		const pageObj = {
			pageSize: TABLE_SIZE_12,
			pageNumber: 0,
		};
		updateTableQuery(pageObj);
	}, []);

	const columns: readonly Column[] = [
		{
			id: 'groupName',
			label: 'Obligations',
			isSortable: true,
			disablePadding: true,
			cellClassName: styles['text-icon-wrapper'],
			render: (params: IGroupsTableList) => (
				<div className={styles.gridContainer}>
					<Typography
						className={styles['text-section']}
						data-testid="theme-link"
						onClick={() => navigateToMappingDetails(params)}
					>
						<Tooltip title={params.groupName}>
							<span>{params.groupName}</span>
						</Tooltip>
						<NotificationStatusIcon
							date={params?.reviewByDate}
							className={styles['icon-date']}
						/>
					</Typography>
				</div>
			),
		},
		{
			id: 'business Unit',
			label: 'Business Unit',
			disablePadding: true,
			isSortable: false,
			cellClassName: styles['bu-owner-styling'],
			render: (params: IGroupsTableList) => (
				<div className={styles['text-multi-select-values']}>
					{getAssociatedBusinessUnitNames(params).map(
						(item: any, index: any) => {
							if (index === 0) {
								return (
									<div
										className={styles['text-down']}
										data-testid="business-unit"
										key={item.id}
									>
										{item.name ?? ''}
									</div>
								);
							}
							return (
								<div
									className={styles['text-down']}
									data-testid="business-unit"
									key={item.id}
								>
									<br />
									{item.name ?? ''}
								</div>
							);
						},
					)}
				</div>
			),
			isCellHoverEditable: checkRole,
			renderHoverEditCell: (params: IGroupsTableList) => (
				<div
					className={styles['text-section-dropdown']}
					data-testid="drop-down"
				>
					<BusinessUnitDropdown
						businessUnitAssociations={params.businessUnitDetailDTOS || []}
						businessUnitOptions={allBusinessUnits}
						assignedUserOptions={assignedUsers}
						saveHandler={(val: IBUDropDownOptions[]) =>
							saveMappingDetails(params, val)
						}
					/>
				</div>
			),
			isCellEditable: checkRole,
			renderEditCell: (params: IGroupsTableList) => (
				<div
					className={styles['text-section-dropdown']}
					data-testid="drop-down"
				>
					<BusinessUnitDropdown
						businessUnitAssociations={params.businessUnitDetailDTOS || []}
						businessUnitOptions={allBusinessUnits}
						assignedUserOptions={assignedUsers}
						saveHandler={(val: IBUDropDownOptions[]) =>
							saveMappingDetails(params, val)
						}
					/>
				</div>
			),
		},
		{
			id: 'Assigned to',
			label: 'Assigned to',
			disablePadding: true,
			isSortable: false,
			render: (params: IGroupsTableList) => (
				<div className={styles['text-multi-select-values']}>
					{getAssociatedAssignedUsers(params).map((item: any, index: any) => {
						if (index === 0) {
							return (
								<div className={styles['text-down']} key={item.id}>
									{item.name ?? ''}
								</div>
							);
						}
						return (
							<div className={styles['text-down']} key={item.id}>
								<br />
								{item.name ?? ''}
							</div>
						);
					})}
				</div>
			),
		},
		{
			id: 'assignedUserName',
			label: 'Owner',
			disablePadding: true,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography className={styles['text-owner-common']}>
					{params?.assignedUserName}
				</Typography>
			),
			cellClassName: styles['owner-styling'],
			isCellHoverEditable: checkRole,
			renderHoverEditCell: (params: IGroupsTableList) => {
				getMappingOwnerData(params.taskStatus || '');
				return (
					<SelectDropdown
						label="Owner"
						name={`mapping-name-${params.groupId}`}
						id="mapping-owner"
						options={getMappingOwnerList(params.taskStatus || '')}
						mode="standard"
						showLabel={false}
						inputProps={{
							'data-testid': 'regulation-owner',
						}}
						value={params.assignedUserId}
						onChange={onMappingOwnerChange(params)}
						className={styles['select-regulation-owner']}
					/>
				);
			},
			isCellEditable: checkRole,
			renderEditCell: (params: IGroupsTableList) => (
				<SelectDropdown
					label="Owner"
					name={`mapping-name-${params.groupId}`}
					id="mapping-owner"
					options={getMappingOwnerList(params.taskStatus || '')}
					mode="standard"
					showLabel={false}
					inputProps={{
						'data-testid': 'regulation-owner',
					}}
					value={params.assignedUserId}
					onChange={onMappingOwnerChange(params)}
					className={styles['select-regulation-owner']}
				/>
			),
		},
		{
			id: 'createdByName',
			label: 'Created By',
			disablePadding: true,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography
					className={styles['text-priorty-common']}
					date-testid="firstName"
				>
					{params?.createdByName || '-'}
				</Typography>
			),
		},
		{
			id: 'taskStatus',
			label: 'Status',
			disablePadding: true,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography
					data-testid="group-status"
					className={styles['text-priorty-common']}
				>
					{params?.taskStatus || '--'}
				</Typography>
			),
		},
		{
			id: 'priority',
			label: 'Priority',
			disablePadding: true,
			isSortable: true,
			render: (params: IGroupsTableList) => (
				<Typography
					data-testid="group-priority"
					className={styles['text-priorty-common']}
				>
					{params?.priority || '--'}
				</Typography>
			),
		},
		{
			id: 'reviewByDate',
			label: 'Due Date',
			disablePadding: true,
			isSortable: true,
			render: (params: any) => (
				<Typography
					className={styles['text-priorty-common']}
					data-testid="dueDate"
				>
					{dateFullYearFormat(params?.reviewByDate || '') || '--'}
				</Typography>
			),
		},
	];

	return (
		<Box data-testid="allregulations-main-box">
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				lg={12}
				xl={12}
				className={styles['table-area']}
			>
				<SelectableHOC
					count={selectedGroups.length}
					styleClass={styles['width-change']}
					element={
						<Box sx={{ display: 'flex' }} className={styles['hoc-dropdowns']}>
							<Box className={styles['flex-action-elements']}>
								<BusinessUnitDropdown
									businessUnitAssociations={[]}
									businessUnitOptions={allBusinessUnits}
									assignedUserOptions={assignedUsers}
									isMultiSelect
									saveHandler={(
										val: IBUDropDownOptions[],
										enableApply?: boolean,
									) => saveHandler(val, enableApply ?? false)}
								/>
							</Box>
							<Box className={styles['flex-action-button-elements']}>
								<ButtonComp
									btnType="primary"
									clickHandler={onApplyClick}
									data-testid="bulk-apply-btn"
									disabled={!enableApplyButton}
									compClass={styles['apply-button']}
								>
									Apply
								</ButtonComp>
							</Box>
						</Box>
					}
					handleCancelChange={onHandleCancel}
				>
					<Box className={styles['table-area']}>
						<TableComponent
							defaultQuery={tableProps}
							rows={inventoryTableData}
							columns={columns}
							totalItems={totalRecordsCount}
							isCheckBoxEnabled={checkRole}
							defaultSortKey="reviewByDate"
							sortDirection="desc"
							rowHoverEnabled
							onPageHandler={handlePageChange}
							isLoading={isLoading}
							tableStyles={{ minWidth: '46.875em', maxHeight: '100vh' }}
							clearSelection={trigger}
							onSortHandler={handleSortChange}
							rowKey="groupId"
							customErrorMessage={errorMessage}
							onRowChecked={onRowCheckboxSelect}
							enableHeaderColor
							isRenderPagination
						/>
					</Box>
				</SelectableHOC>
			</Grid>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</Box>
	);
};
export default MappingTable;
